import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import styles from "../../assets/styles/Homepage/VedicMantraCourse.module.css";
import mantraImg from "../../assets/images/IMG_130.jpeg";
import { useNavigate } from "react-router-dom";
const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

const API_BASE = `${ENV_API_BASE_URL}/api`;
const ASSET_BASE = `${ENV_API_BASE_URL}`;

const DEFAULT_COURSE = {
  title: "Vedic Mantra Chanting",
  subtitle: "Teacher Training Course",
  price: "$64.99",
  description:
    "Mantra is a sacred utterance, a numinous sound, a syllable, a word or phonemes, or a group of words believed by practitioners to have religious, magical, or spiritual powers and to aid concentration.",
  date: "2nd of every month",
  image: mantraImg,
  bookPath: "/BookingForm",
};

const toAssetUrl = (value) => {
  if (!value) return "";
  if (typeof value !== "string") return value;
  if (value.startsWith("http") || value.startsWith("data:")) return value;
  return `${ASSET_BASE}/${value.replace(/^\/+/, "")}`;
};

const formatDate = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(parsed);
};

/* ===================== */
/* Framer Motion Variants */
/* ===================== */

const sectionVariant = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const imageVariant = {
  hidden: { opacity: 0, x: -40, scale: 0.95 },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const contentVariant = {
  hidden: { opacity: 0, x: 40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.7,
      ease: "easeOut",
      staggerChildren: 0.15,
    },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 25 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const VedicMantraCourse = () => {
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const res = await axios.get(`${API_BASE}/courses/slug/vedic-mantra`);
        setCourse(res.data?.data || null);
      } catch (error) {
        console.error("Failed to load Vedic Mantra course", error);
      }
    };

    fetchCourse();
  }, []);

  const nextBatch = useMemo(() => {
    const batches = course?.batches || [];
    if (!batches.length) return null;
    return (
      batches.find((batch) => Number(batch.availableSeats || 0) > 0) || batches[0]
    );
  }, [course]);

  const content = course?.content || {};
  const overview = content.overview || {};
  const hero = content.hero || {};

  const viewModel = {
    title: course?.title || hero.title || DEFAULT_COURSE.title,
    subtitle: overview.tag || course?.shortTitle || DEFAULT_COURSE.subtitle,
    price: nextBatch?.priceShared || course?.card?.price || DEFAULT_COURSE.price,
    description:
      overview.description ||
      course?.description ||
      DEFAULT_COURSE.description,
    date: formatDate(nextBatch?.startDate) || DEFAULT_COURSE.date,
    image: toAssetUrl(course?.card?.image || course?.banner) || DEFAULT_COURSE.image,
    bookPath: course?.card?.formLink || DEFAULT_COURSE.bookPath,
  };

  return (
    <motion.section
      className={styles.section}
      variants={sectionVariant}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
    >
      <div className={`container ${styles.cardWrapper}`}>
        <div className={styles.card}>

          {/* Left Image */}
          <motion.div
            className={styles.imageBox}
            variants={imageVariant}
          >
            <img src={viewModel.image} alt={viewModel.title} />
          </motion.div>

          {/* Right Content */}
          <motion.div
            className={styles.contentBox}
            variants={contentVariant}
          >
            <motion.h2 className={styles.title} variants={fadeUp}>
              {viewModel.title}
            </motion.h2>

            <motion.p className={styles.subtitle} variants={fadeUp}>
              {viewModel.subtitle}
            </motion.p>

            <motion.h4 className={styles.feeLabel} variants={fadeUp}>
              Course Fee
            </motion.h4>

            <motion.div className={styles.price} variants={fadeUp}>
              {viewModel.price}
            </motion.div>

            <motion.p className={styles.description} variants={fadeUp}>
              {viewModel.description}
            </motion.p>

            <motion.div className={styles.dateRow} variants={fadeUp}>
              <span className={styles.calendarIcon}>📅</span>
              <strong>Date:</strong> {viewModel.date}
            </motion.div>

            <motion.div className={styles.btnGroup} variants={fadeUp}>
              <motion.button
  className={styles.outlineBtn}
  whileHover={{ scale: 1.05 }}
  whileTap={{ scale: 0.95 }}
  onClick={() => navigate("/vedic-mantra")}
>
  More Details
</motion.button>

<motion.button
  className={styles.fillBtn}
  whileHover={{ scale: 1.05 }}
  whileTap={{ scale: 0.95 }}
  onClick={() => navigate(viewModel.bookPath)}
>
  Book Now
</motion.button>

            </motion.div>
          </motion.div>

        </div>
      </div>
    </motion.section>
  );
};

export default VedicMantraCourse;
